import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { HttpParams } from '@angular/common/http';

import { BaseIndexPageComponent } from 'src/app/components/pages/base-index-page/base-index-page.component';
import BaseIndexPageConfig from 'src/app/components/pages/base-index-page/base-index-page.config';
import { DataTableColumnText } from 'src/app/components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { SelectOption } from 'src/app/components/form/select-field/select-field.config';
import ApiOptionsConfig from 'src/app/services/api/api-options.config';
import { IntroducersService } from 'src/app/services/api/introducers.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { AccessControlService } from 'src/app/services/access-control.service';

interface IntroducerRevenueTotals {
  totalOrders: number;
  totalRevenue: number;
}

@Component({
  selector: 'app-introducer-revenue-list-page',
  imports: [CommonModule, ReactiveFormsModule, BaseIndexPageComponent, InputFieldComponent, SelectFieldComponent],
  templateUrl: './introducer-revenue-list-page.component.html',
  styleUrl: './introducer-revenue-list-page.component.scss',
})
export class IntroducerRevenueListPageComponent implements OnInit, OnDestroy {
  @ViewChild(BaseIndexPageComponent, { static: true }) baseIndexPage?: BaseIndexPageComponent;

  baseIndexPageConfig: BaseIndexPageConfig;

  introducerOptions: SelectOption[] = [];
  introducerControl = new FormControl<any>(null);
  isIntroducerSelectLocked = false;
  startingDateControl = new FormControl<string>('');
  filterFromControl = new FormControl<string>('');
  filterToControl = new FormControl<string>('');

  totals: IntroducerRevenueTotals | null = null;
  isLoadingTotals = false;

  private introducers: any[] = [];
  private subscriptions = new Subscription();

  constructor(
    protected introducersService: IntroducersService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
  ) {
    this.baseIndexPageConfig = {
      pageTitle: 'Revenue List',
      cardTitle: 'Revenue List',
      dataTable: {
        data: [],
        pagination: null,
        searchModel: {
          clientName: null,
          proposalName: null,
          step: null,
          projectCode: null,
          projectName: null,
          finalPriceMin: null,
          finalPriceMax: null,
          revenueMin: null,
          revenueMax: null,
        },
        columns: [
          new DataTableColumnText({
            attribute: 'clientName',
            header: 'Client Name',
            filter: 'text',
            value: (item: any) => item.clientName,
          }),
          new DataTableColumnText({
            attribute: 'proposalDate',
            header: 'Proposal Date',
            value: (item: any) => this.formatDate(item.proposalDate),
          }),
          new DataTableColumnText({
            attribute: 'proposalName',
            header: 'Proposal Name',
            filter: 'text',
            value: (item: any) => item.proposalName,
          }),
          new DataTableColumnText({
            attribute: 'step',
            header: 'Step',
            filter: 'text',
            value: (item: any) => item.step,
          }),
          new DataTableColumnText({
            attribute: 'projectCode',
            header: 'Cod. Prj.',
            filter: 'text',
            value: (item: any) => item.projectCode,
          }),
          new DataTableColumnText({
            attribute: 'projectDate',
            header: 'Project Date',
            value: (item: any) => this.formatDate(item.projectDate),
          }),
          new DataTableColumnText({
            attribute: 'projectName',
            header: 'Project Name',
            filter: 'text',
            value: (item: any) => item.projectName,
          }),
          new DataTableColumnText({
            attribute: 'finalPrice',
            header: 'Final Price',
            filter: 'number-range',
            filterMinAttribute: 'finalPriceMin',
            filterMaxAttribute: 'finalPriceMax',
            sortable: false,
            value: (item: any) => this.formatCurrency(item.finalPrice),
          }),
          new DataTableColumnText({
            attribute: 'revenue',
            header: 'Revenue',
            filter: 'number-range',
            filterMinAttribute: 'revenueMin',
            filterMaxAttribute: 'revenueMax',
            sortable: false,
            value: (item: any) => this.formatCurrency(item.revenue),
          }),
        ],
      },
      apiListService: (options: ApiOptionsConfig) => this.introducersService.listIntroducerRevenues(options),
      responseListModelName: 'revenues',
    };
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.mainLayoutService.activeProfile$.subscribe(profileCode => {
        this.isIntroducerSelectLocked = !this.accessControlService.hasFeatureActionAccess(
          profileCode, 'INTRODUCER_SELECT_FILTER', 'modify'
        );
        if (this.isIntroducerSelectLocked) {
          this.introducerControl.disable({ emitEvent: false });
        } else {
          this.introducerControl.enable({ emitEvent: false });
        }
      })
    );
    this.filterFromControl.valueChanges.subscribe(() => this.onDateRangeChange());
    this.filterToControl.valueChanges.subscribe(() => this.onDateRangeChange());

    this.loadIntroducers();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onIntroducerChange(introducerId: any): void {
    this.introducerControl.setValue(this.toId(introducerId), { emitEvent: false });
    this.applySelectedIntroducer();
    this.refresh();
  }

  onFiltersChanged(params: HttpParams): void {
    this.loadTotals(params);
  }

  get hasSelection(): boolean {
    return this.introducerControl.value !== null && this.introducerControl.value !== undefined;
  }

  private loadIntroducers(): void {
    this.introducersService
      .list()
      .subscribe((response: any) => {
        this.introducers = response?.data?.introducers ?? [];
        this.introducerOptions = this.introducers.map((introducer: any) => ({
          key: introducer.introducerId,
          value: introducer.name,
        }));

        if (!this.introducers.length) {
          return;
        }

        this.introducerControl.setValue(this.introducers[0].introducerId, { emitEvent: false });
        this.applySelectedIntroducer();
        this.refresh();
      });
  }

  private applySelectedIntroducer(): void {
    const introducer = this.selectedIntroducer();
    this.startingDateControl.setValue(
      introducer?.contractStartTime ? this.formatDate(introducer.contractStartTime) : ''
    );
  }

  private selectedIntroducer(): any {
    return this.introducers.find(
      (introducer: any) => String(introducer.introducerId) === String(this.introducerControl.value)
    );
  }

  private onDateRangeChange(): void {
    if (!this.hasSelection) {
      return;
    }
    this.refresh();
  }

  private refresh(): void {
    const extraParams = this.buildExtraParams();
    this.baseIndexPage?.reload(extraParams);
    this.loadTotals(this.buildFilterParams(extraParams));
  }

  private buildExtraParams(): Record<string, any> {
    return {
      introducerId: this.introducerControl.value,
      startDate: this.normalizeDate(this.filterFromControl.value),
      endDate: this.normalizeDate(this.filterToControl.value),
    };
  }

  private buildFilterParams(extraParams: Record<string, any>): HttpParams {
    let params = new HttpParams();

    const searchModel = this.baseIndexPageConfig.dataTable.searchModel;
    Object.keys(searchModel).forEach((key) => {
      if (searchModel[key]) {
        params = params.set(key, searchModel[key]);
      }
    });

    Object.keys(extraParams).forEach((key) => {
      const value = extraParams[key];
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value);
      }
    });

    return params;
  }

  private loadTotals(params: HttpParams): void {
    this.isLoadingTotals = true;

    this.introducersService.introducerRevenueTotals({ params }).subscribe({
      next: (response: any) => {
        this.totals = response?.data?.totals ?? null;
        this.isLoadingTotals = false;
      },
      error: () => {
        this.totals = null;
        this.isLoadingTotals = false;
      },
    });
  }

  private normalizeDate(value: string | null | undefined): string {
    const trimmed = (value ?? '').trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : '';
  }

  private toId(value: any): any {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? value : parsed;
  }

  private formatDate(value: any): string {
    if (!value) {
      return '';
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
  }

  formatCurrency(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const amount = Number(value);
    if (Number.isNaN(amount)) {
      return '';
    }
    return `€ ${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
}
